import { ACard, AMultiSelectFilter, AMultiSelectOption, AMultiSelectOptionId, ASectionTitle } from "@atiautomacao/ati-ui-library";
import { Box, Grid, Skeleton, Stack } from "@mui/material";
import { useAppSelector } from "../../../Config/Hooks";
import { RootState } from "../../../Config/Store";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import SynopticCard, { SynoppticCardData } from "./SynopticCard";
import { useSynopticDataList } from "./SynopticReactQuery";
import { useCallback, useEffect, useMemo, useState } from "react";
import { alarmSound } from "../Alarms/AlarmPage";
import { usePersistedSelection } from "../../../Shared/Hooks/usePersistedSelection";
import { POWER_STATION_IDS_FILTER_STORAGE_KEY } from "../../../Config/Constants";

export interface QueryData {
    status: string,
    data?: any,
    error?: any,
}

export const transformToDTO = (data: any) => {
    const equipmentStatusDefault = {
        name: 'Default',
        color: 'c9c9c9'
    };
    const trackersTypeIds = [8, 22, 23];

    return {
        powerStation: {
            id: data?.powerStationId,
            name: data?.name,
            hasAlarmSound: data?.hasAlarmSound,
            status: data?.status,
            inverter: {
                status: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.status ?? equipmentStatusDefault,
                isOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.offline ?? false,
                allEquipmentOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.allEquipmentOffline ?? false,
                stoppedCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.countCriticalAlarms ?? 0,
                pendingCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.pendingCriticalCount ?? 0,
                highCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.countHighAlarms ?? 0,
                withoutCommunicationCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 2)?.countWithoutCommunication ?? 0
            },
            relay: {
                status: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.status ?? equipmentStatusDefault,
                isOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.offline ?? false,
                allEquipmentOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.allEquipmentOffline ?? false,
                stoppedCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.countCriticalAlarms ?? 0,
                pendingCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.pendingCriticalCount ?? 0,
                highCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.countHighAlarms ?? 0,
                withoutCommunicationCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 4)?.countWithoutCommunication ?? 0
            },
            solarimetric: {
                status: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.status ?? equipmentStatusDefault,
                isOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.offline ?? false,
                allEquipmentOffline: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.allEquipmentOffline ?? false,
                stoppedCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.countCriticalAlarms ?? 0,
                pendingCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.pendingCriticalCount ?? 0,
                highCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.countHighAlarms ?? 0,
                withoutCommunicationCount: data.synopticEquipmentList.find((eq:any) => eq?.equipmentTypeId === 6)?.countWithoutCommunication ?? 0
            },
            trackers: {
                status: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.status ?? equipmentStatusDefault,
                isOffline: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.offline ?? false,
                allEquipmentOffline: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.allEquipmentOffline ?? false,
                stoppedCount: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.countCriticalAlarms ?? 0,
                pendingCount: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.pendingCriticalCount ?? 0,
                highCount: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.countHighAlarms ?? 0,
                withoutCommunicationCount: data.synopticEquipmentList.find((eq:any) => trackersTypeIds.includes(eq?.equipmentTypeId))?.countWithoutCommunication ?? 0
            }
        }
    } as SynoppticCardData;
};

export const cutStatus = (status: string) => {
    if(status === undefined) return "Não configurado"
    if (status.includes(' - ')) {
        const parts = status.split(' - ');
        return  parts[0];
    }
    return status
}

export const STATUS_PRIORITY: Record<string, number> = {
    "Alarmado": 0,
    "Não responde": 1,
    "Sincronizando": 2,
    "Normalizado": 3,
    "Inibido": 4,
    "Não configurado": 5,
};


export default function SynopticPage() {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const entitiesDashboard = useAppSelector(
        (state: RootState) => state.dashboardNavigationMenu.entities
    );
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...entitiesDashboard]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map(e => ({ id: e.id, label: e.name })),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map(o => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const filteredPowerStationIds = useMemo<number[]>(
        () => isInitialized
            ? selectedPowerStationIds.map(id => Number(id))
            : entitiesDashboard.map(e => e.id),
        [isInitialized, selectedPowerStationIds, entitiesDashboard]
    );

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) return;
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const queries = useSynopticDataList(filteredPowerStationIds, userConfig.mainUpdateInterval);
    const [hasAlarmSound, setHasAlarmSound] = useState(false);

    const allQueryStates : QueryData[] = queries.map(q => {
        if (q.isSuccess && q.data?.data) {
            return { status: 'success', data: transformToDTO(q.data.data) };
        } else if (q.isLoading) {
            return { status: 'loading' };
        } else if (q.isError) {
            return { status: 'error', error: q.error };
        } else {
            return { status: 'idle' }; // fallback, se necessário
        }
    });

    


    function sortByStatusAndName(data: QueryData[]): QueryData[] {
        return [...data].sort((a, b) => {
            if(a.data === undefined || b.data === undefined) return 0

            const statusNameA = cutStatus(a.data.powerStation.status.name)
            const statusNameB = cutStatus(b.data.powerStation.status.name)

            const priorityA = STATUS_PRIORITY[statusNameA] ?? Number.MAX_SAFE_INTEGER;
            const priorityB = STATUS_PRIORITY[statusNameB] ?? Number.MAX_SAFE_INTEGER;

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            return statusNameA.localeCompare(statusNameB);
        });
    }
    useEffect(() => {
        if (hasAlarmSound) {
            const intervalo = setInterval(() => {
                alarmSound();
            }, 2000);

            return () => clearInterval(intervalo);
        }
    },[hasAlarmSound]);

    const hasSound = allQueryStates.filter(q => q.status === 'success' && q.data?.powerStation.hasAlarmSound).length > 0;
    if (hasSound !== hasAlarmSound) {
        setHasAlarmSound(hasSound);
    }

    const ordenedData = sortByStatusAndName(allQueryStates) //synopticDataList MOCK_DATA

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", ml: 3, mr: 3, mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ASectionTitle
                        titleLabel="Sinóptico das Usinas"
                        subTitleLabel="Monitore o status de todas as suas usinas em uma só tela"
                        styleContainerSubtitle={{paddingTop:0, marginBottom:0}}
                        contentChildrenStyle={{paddingTop:0, marginTop:-2}}
                        contentChildren={
                            <Stack direction="row" sx={{ flexShrink: 0, paddingTop: 0, alignItems: "center" }}>
                                <AMultiSelectFilter
                                    options={powerStationOptions}
                                    selectedIds={selectedPowerStationIds}
                                    onApply={handleApplyPowerStationFilter}
                                    triggerLabel="Usinas"
                                    searchPlaceholder="Buscar usina..."
                                />
                            </Stack>
                        }
                    />
                </Box>
            </Box>
            {ordenedData ?
            <Grid container spacing={2} sx={{ width: '100%', margin: 0, marginLeft: {xs: 6,sm: 8, md: 0,lg: 1.5, xl: 1.5}, marginTop: 1 }}>

                {ordenedData.map((data, idx) => (
                    <>

                            <Grid sx={{marginLeft: 2, marginTop: 1}} item sm={3} md={openSubMenu ? 4.8 : 3} lg={openSubMenu ? 2.7 : 2.2} xl={openSubMenu ? 1.8 : 1.6} key={idx} >
                                {data.status === 'loading' ?
                                <Skeleton animation="wave" height={90} variant="rounded" width={190} />
                                :
                                data.status === 'error' ?
                                    <ACard styleProps={{cardStyle: {
                                            height: 90,
                                            padding: 0,
                                            width: 190,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            left: '15px'
                                        }}}>
                                        <DataNotFound boxStyle={{ height: 90, width: 190, left: '15px' }} titleStyle={{marginRight: 25}} iconStyle={{marginRight: 20}}/>
                                    </ACard>
                                    :
                                    <SynopticCard synopticData={data.data}/>}
                            </Grid>

                    </>
                ))
                }
            </Grid>
            : <Grid container spacing={1}>
                <ACard>
                    Dados não encontrados
                </ACard>
            </Grid>
            }
        </>
    )
}